import { motion } from "framer-motion";
import {
  ArrowRight, Calendar, User, Package, Ship, Trash2,
  Upload, FileText, Copy, RotateCcw, Printer, Mail, Warehouse,
} from "lucide-react";
import { InlineCountryFlag } from "../shipment/CountryBadge";

const PORT_COUNTRY_MAP = {
  USORF: "US", USCHI: "US", USLAX: "US", USNYC: "US", USSEA: "US",
  INNSA: "IN", INMUN: "IN", INMAA: "IN", INBOM: "IN",
  CNSHA: "CN", CNNGB: "CN", CNSZX: "CN", CNTAO: "CN",
  SGSIN: "SG", AEDXB: "AE", GBFXT: "GB", NLRTM: "NL",
  DEHAM: "DE", FRLEH: "FR", JPYOK: "JP", KRPUS: "KR",
  AUSYD: "AU", CAVAN: "CA", BRSSZ: "BR", MXZLO: "MX",
};

function getCountryCodeByPort(portCode) {
  if (!portCode || typeof portCode !== "string") return null;
  const code = portCode.trim().toUpperCase();
  return PORT_COUNTRY_MAP[code] ?? (code.length >= 2 ? code.slice(0, 2) : null);
}

function getLocationName(location) {
  if (!location) return "N/A";
  return location.split(",")[0].replace(/\[.*\]/g, "").trim();
}

function formatDate(dateStr) {
  if (!dateStr) return "TBD";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

const STATUS_PILL = {
  Active:       "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-700",
  Pending:      "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-700",
  Closed:       "bg-gray-100 text-gray-600 ring-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700",
  Cancelled:    "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:ring-rose-700",
  PENDING:      "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-700",
  COMPLETED:    "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-700",
  "IN TRANSIT": "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-700",
 
};

function IconBtn({ icon: Icon, label, tone = "default", onClick }) {
  const toneCls = {
    default: "text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700",
    danger:  "text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:text-rose-400 dark:hover:text-rose-300 dark:hover:bg-rose-900/30",
    primary: "text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/30",
    warning: "text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:text-amber-300 dark:hover:bg-amber-900/30",
  }[tone];
  return (
    <button title={label} onClick={onClick}
      className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ${toneCls}`}>
      <Icon className="w-4 h-4" />
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function resolve(obj, key, fallback = null) {
  if (!obj || !key) return fallback;
  return key.split(".").reduce((acc, k) => acc?.[k] ?? null, obj) ?? fallback;
}

function resolveField(record, mapEntry, fallback = null) {
  if (!mapEntry) return fallback;
  const keys = Array.isArray(mapEntry) ? mapEntry : [mapEntry];
  for (const key of keys) {
    const val = resolve(record, key);
    if (val !== null && val !== undefined && val !== "") return val;
  }
  return fallback;
}

export const DEFAULT_FIELD_MAP = {
  id:             "soId",
  number:         ["soNumber", "poNumber", "bookingNumber"],
  polCode:        "polCode",
  podCode:        "podCode",
  pol:            ["pol", "portOfLoad"],
  pod:            ["pod", "portOfDischarge"],
  orderDate:      ["orderDate", "soDate", "poDate", "bookingDate"],
  shipByDate:     ["shipByDate", "expectedDelivery", "etd"],
  customer:       ["customer", "vendor", "shipper"],
  customerEmail:  ["customerEmail", "vendorEmail"],
  createdBy:      "createdBy",
  createdOn:      "createdOn",
  warehouse:      ["warehouse", "originWarehouse"],
  sku:            ["allSku", "itemName", "description"],
  quantity:       "quantity",
  status:         "status",
  customerLabel:   "Customer",
  orderDateLabel:  "Order Date",
  shipByDateLabel: "Ship By Date",
  skuLabel:        "SKU / Item",
  extraFields:     [],
};

export const SALES_ORDER_MAP = {
  ...DEFAULT_FIELD_MAP,
  id:             "soId",
  number:         "soNumber",
  orderDate:      ["orderDate", "soDate"],
  shipByDate:     "shipByDate",          // ✅ shown
  customer:       "customer",
  warehouse:      "warehouse",           // ✅ shown
  sku:            "allSku",
  status:         ["loadStatus", "status"],
  createdBy:      "createdBy",           // ✅ shown
  customerLabel:   "Customer",
  orderDateLabel:  "Order Date",
  shipByDateLabel: "Ship By Date",
  skuLabel:        "SKU Code",
  extraFields: [
    { label: "Destination", key: "destination" },
  ],
  editPath:  (id) => `/sales-order/edit/${id}`,
  printPath: (id) => `/sales-order/print/${btoa(String(id))}`,
  emailPath: (id) => `/mail/compose?type=SalesOrder&id=${id}`,
};

export const PURCHASE_ORDER_MAP = {
  ...DEFAULT_FIELD_MAP,
  id:             "purchaseOrderId",
  number:         "poNumber",
  orderDate:      ["orderDate", "poDate"],
  shipByDate:     null,
  customer:       ["vendor", "supplier", "customer"],
  customerEmail:  "vendorEmail",
  warehouse:      "warehouse",
  sku:            "allSku",
  status:         "purchaseOrderStatus",
  createdBy:      "createdBy",
  // ▼ hide the POL → POD route row in the header
  polCode:        null,
  podCode:        null,
  pol:            null,
  pod:            null,
  // ▲
  customerLabel:   "Vendor",
  orderDateLabel:  "PO Date",
  shipByDateLabel: "Expected Delivery",
  skuLabel:        "SKU Code",
  extraFields: [
    { label: "Consignee", key: "consignee" },
    { label: "Supplier",  key: "supplier" },
    { label: "Ship To",   key: "destination" }, // destination lives here now
  ],
  editPath:  (id) => `/purchase-order/edit/${id}`,
  printPath: (id) => `/purchase-order/print/${btoa(String(id))}`,
  emailPath: (id) => `/mail/compose?type=PurchaseOrder&id=${id}`,
};

export const LOAD_PLAN_MAP = {
  ...DEFAULT_FIELD_MAP,
  id:             "loId",
  number:         "loNumber",
  orderDate:      "loDtae",
  shipByDate:     "loShipByDate",        // ✅ shown
  customer:       "customer",
  customerEmail:  null,                  // ❌ hidden
  warehouse:      null,                  // ❌ hidden
  sku:            null,
  quantity:       null,
  status:         "status",
  polCode:        null,
  podCode:        null,
  pol:            null,
  pod:            ["destinationCountry"],
  createdBy:      null,                  // ❌ hides Created By
  customerLabel:   "Customer",
  orderDateLabel:  "Order Date",
  shipByDateLabel: "Ship By Date",
  skuLabel:        null,                 // ❌ hidden
  extraFields: [
    { label: "Shipment #",        key: "shipmentNumber" },
    { label: "Sales Order #",     key: "loSoNumber" },
    { label: "Purchase Order #",  key: "poNumber" },
    { label: "Consignee",         key: "consignee" },
  ],
  editPath:  (id) => `/load-plan/edit/${id}`,
  printPath: (id) => `/load-plan/print/${btoa(String(id))}`,
  emailPath: (id) => `/mail/compose?type=LoadPlan&id=${id}`,
};

// ─── Main Component ───────────────────────────────────────────────────────────
const OrderCard = ({
  record,
  fieldMap: fieldMapOverride,
  showDeletedRecords,
  navigate,
  t = (k) => k,
  numberLabel = "",
  onEdit,
  onOpenFileUpload,
  onOpenNotes,
  onDelete,
  onRestore,
  onCopy,
}) => {
  const fm = { ...DEFAULT_FIELD_MAP, ...fieldMapOverride };

  const id            = resolveField(record, fm.id);
  const number        = resolveField(record, fm.number, "—");
  const status        = resolveField(record, fm.status, "Pending");
  const polCode       = resolveField(record, fm.polCode);
  const podCode       = resolveField(record, fm.podCode);
  const pol           = resolveField(record, fm.pol, null);
  const pod           = resolveField(record, fm.pod, null);
  const orderDate     = resolveField(record, fm.orderDate);
  const shipByDate    = resolveField(record, fm.shipByDate);
  const customer      = resolveField(record, fm.customer, "—");
  const customerEmail = resolveField(record, fm.customerEmail);
  const createdBy     = resolveField(record, fm.createdBy);
  const createdOn     = resolveField(record, fm.createdOn);
  const warehouse     = resolveField(record, fm.warehouse, null);
  const sku           = resolveField(record, fm.sku, null);
  const quantity      = resolveField(record, fm.quantity);

  const buildPath = (key, fallback) => {
    const entry = fm[key];
    if (typeof entry === "function") return entry(id);
    if (typeof entry === "string")   return entry;
    return fallback;
  };
  const editPath  = buildPath("editPath",  `/record/edit/${id}`);
  const printPath = buildPath("printPath", `/record/print/${id}`);
  const emailPath = buildPath("emailPath", `/mail/compose?id=${id}`);

  function extractPortCode(locationStr) {
    if (!locationStr) return null;
    const match = locationStr.match(/\[([A-Z]{2,5})\]/);
    return match ? match[1] : null;
  }
  const originCode = getCountryCodeByPort(polCode) ?? getCountryCodeByPort(extractPortCode(pol)) ?? null;
  const destCode   = getCountryCodeByPort(podCode) ?? getCountryCodeByPort(extractPortCode(pod)) ?? null;

  const statusKey = status?.toString();
  const statusCls = STATUS_PILL[statusKey] ??
    STATUS_PILL[statusKey?.toUpperCase()] ??
    "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-700";

  // Show route only when the field map includes POL/POD (e.g. Sales Order)
  const hasRoute = (fm.pol !== null || fm.polCode !== null) && (pol || pod);

  // ── Build flat field list ──────────────────────────────────────────────────
  const fieldNodes = [];

  // 1. Customer — always shown
  fieldNodes.push(
    <Field key="customer" label={fm.customerLabel ?? "Customer"}>
      <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 truncate block">
        {customer}
      </span>
      {customerEmail && (
        <div className="text-[10.5px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">
          {customerEmail}
        </div>
      )}
    </Field>
  );

  // 2. Warehouse — skip when fm.warehouse === null
  if (fm.warehouse !== null) {
    fieldNodes.push(
      <Field key="warehouse" label="Warehouse">
        <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
          <Warehouse className="w-3 h-3 text-gray-400 dark:text-gray-500 shrink-0" />
          <span className="truncate">{warehouse || "—"}</span>
        </span>
      </Field>
    );
  }

  if (fm.shipByDate !== null) {
    fieldNodes.push(
      <div key="dates" className="grid grid-cols-2 gap-3">
        <Field label={fm.orderDateLabel ?? "Order Date"}>
          <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
            <Calendar className="w-3 h-3 text-gray-400 dark:text-gray-500" />
            {formatDate(orderDate)}
          </span>
        </Field>
        <Field label={fm.shipByDateLabel ?? "Ship By Date"}>
          <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
            <Calendar className="w-3 h-3 text-gray-400 dark:text-gray-500" />
            {formatDate(shipByDate)}
          </span>
        </Field>
      </div>
    );
  } else {
    fieldNodes.push(
      <Field key="orderDate" label={fm.orderDateLabel ?? "Order Date"}>
        <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
          <Calendar className="w-3 h-3 text-gray-400 dark:text-gray-500" />
          {formatDate(orderDate)}
        </span>
      </Field>
    );
  }

  // 4. SKU — skip when fm.skuLabel === null
  if (fm.skuLabel !== null) {
    fieldNodes.push(
      <Field key="sku" label={fm.skuLabel ?? "SKU / Item"}>
        <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
          <Package className="w-3 h-3 text-gray-400 dark:text-gray-500 shrink-0" />
          <span className="truncate">
            {sku || "—"}
            {quantity && (
              <span className="text-gray-500 dark:text-gray-400"> · Qty {quantity}</span>
            )}
          </span>
        </span>
      </Field>
    );
  }


  if (fm.createdBy !== null) {
    fieldNodes.push(
      <Field key="createdBy" label="Created By">
        <span className="text-[12.5px] font-medium inline-flex items-center gap-1.5">
          <User className="w-3 h-3 text-gray-400 dark:text-gray-500" />
          {!createdBy ? (
            <span className="text-gray-400 dark:text-gray-500 italic">Unassigned</span>
          ) : (
            <span className="text-gray-900 dark:text-gray-100">{createdBy}</span>
          )}
        </span>
      </Field>
    );
  }

  // 6. Extra fields — always rendered, empty shows "—"
  (fm.extraFields ?? []).forEach(({ label, key }) => {
    const val = resolveField(record, key, "—");
    fieldNodes.push(
      <Field key={label} label={label}>
        <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 truncate block">
          {val}
        </span>
      </Field>
    );
  });

  // Slice evenly into 3 columns
  const colSize = Math.ceil(fieldNodes.length / 3);
  const bodyColumns = [
    fieldNodes.slice(0, colSize),
    fieldNodes.slice(colSize, colSize * 2),
    fieldNodes.slice(colSize * 2),
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="group relative bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800
                 rounded-xl shadow-sm hover:shadow-lg hover:-translate-y-0.5
                 transition-all duration-200 ease-out overflow-hidden"
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="px-5 pt-4 pb-3 flex flex-wrap items-start justify-between gap-3
                         border-b border-gray-100 dark:border-gray-800">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="text-[14px] font-bold text-gray-900 dark:text-gray-50 cursor-pointer
                         hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              onClick={() => !showDeletedRecords && onEdit?.(record)}
            >
            
              {number} 🔗
            </span>
            <span className={`inline-flex items-center px-2 h-5 rounded-full text-[10px] font-bold ring-1 ${statusCls}`}>
              {status}
            </span>
          </div>

          {hasRoute && (
            <div className="mt-1.5 flex items-center gap-2 text-[12.5px]">
              {originCode && <InlineCountryFlag code={originCode} />}
              <span className="font-semibold text-gray-900 dark:text-gray-100 font-mono">
                {getLocationName(pol)}
              </span>
              <ArrowRight className="w-3.5 h-3.5 text-gray-400 dark:text-gray-600" />
              {destCode && <InlineCountryFlag code={destCode} />}
              <span className="font-semibold text-gray-900 dark:text-gray-100 font-mono">
                {getLocationName(pod)}
              </span>
            </div>
          )}
        </div>
      </header>

      {/* ── Body ─────────────────────────────────────────────────────────────── */}
      <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-3">
        {bodyColumns.map((col, i) => (
          <div key={i} className="space-y-3 min-w-0">
            {col}
          </div>
        ))}
      </div>

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
     <footer className="flex flex-wrap items-center justify-between gap-3 px-3 py-2
                   bg-gray-50 dark:bg-gray-800/60
                   border-t border-gray-100 dark:border-gray-800 rounded-b-xl">

  {/* Left meta — hidden when map disables createdBy (e.g. Load Plan) */}
  {fm.createdBy !== null && (
    <div className="flex items-center gap-3 flex-wrap">
      <span className="flex items-center gap-1.5 text-[10.5px] text-gray-500 dark:text-gray-400">
        <User className="w-3 h-3" />
        {t("createdBy")}:{" "}
        <span className="font-medium text-gray-700 dark:text-gray-300">
          {createdBy ?? "—"}
        </span>
      </span>
      <div className="h-3 w-px bg-gray-300 dark:bg-gray-700" />
      <span className="text-[10.5px] text-gray-500 dark:text-gray-400">
        Created: {formatDate(createdOn)}
      </span>
    </div>
  )}

        <div className="flex items-center gap-0.5">
          {showDeletedRecords ? (
            <IconBtn icon={RotateCcw} label={t("restore")} tone="warning" onClick={onRestore} />
          ) : (
            <>
              <IconBtn icon={Printer}  label="Print"           onClick={() => window.open(printPath)} />
              <IconBtn icon={Mail}     label={t("mail")}       tone="primary" onClick={() => navigate(emailPath)} />
              <span className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />
              <IconBtn icon={Upload}   label={t("upload")}     onClick={onOpenFileUpload} />
              <IconBtn icon={FileText} label={t("documents")}  onClick={onOpenNotes} />
              {onCopy && (
                <>
                  <span className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />
                  <IconBtn icon={Copy} label={t("copy")} onClick={() => onCopy(id)} />
                </>
              )}
              <span className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />
              <IconBtn icon={Trash2} label={t("delete")} tone="danger" onClick={onDelete} />
            </>
          )}

          {!showDeletedRecords && (
            <button
              onClick={() => onEdit?.(record)}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[11.5px] font-semibold
                         text-blue-700 bg-blue-50 hover:bg-blue-100
                         dark:text-blue-300 dark:bg-blue-900/30 dark:hover:bg-blue-900/50
                         transition-colors ml-1"
            >
              Open <ArrowRight className="w-3 h-3" />
            </button>
          )}
        </div>
      </footer>
    </motion.div>
  );
};

export default OrderCard;